/**
 * Downsize hotlinked company logos at the CDN edge where the host supports it.
 *
 * Some search hits carry an external `hostedLogoUrl` pointing at a full-resolution
 * asset (worst observed: a 551KB JPEG from duunitori.imgix.net rendered at 38×62px —
 * 2026-08 audit P0-6). imgix hosts accept sizing/format params on the URL, so request
 * a device-pixel-ratio-scaled rendition instead of the original.
 *
 * Non-imgix hosts are returned unchanged — arbitrary hosts can't be assumed to
 * tolerate unknown query params.
 */
export function optimizeHostedLogoUrl(url: string, displayPx: number): string {
  const trimmed = (url || '').trim();
  if (!trimmed) return trimmed;
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return trimmed;
  }
  if (!parsed.hostname.toLowerCase().endsWith('.imgix.net')) {
    return trimmed;
  }
  // 2x the CSS pixel size covers retina displays; fit=max never upscales.
  const targetPx = Math.max(1, Math.round(displayPx * 2));
  parsed.searchParams.set('w', String(targetPx));
  parsed.searchParams.set('h', String(targetPx));
  parsed.searchParams.set('fit', 'max');
  parsed.searchParams.set('auto', 'format,compress');
  return parsed.toString();
}
